import {
  Instance as PopperInstance,
  Placement as PopperPlacement,
  Rect as PopperRect,
} from "@popperjs/core";
import { logger } from "@devforce/trailhead-client-logger";

import type { PopoverState, PopoverPlacement, PopoverOptions } from "./types.d";
export type { PopoverState, PopoverPlacement, PopoverOptions };

const DEFAULT_PLACEMENT = "top";
const NUBBIN_SIZE = 14;
const DEFAULT_STATE = {
  isOpen: false,
  isDisabled: false,
  isReady: false,
  hideQueued: false,
};

export class Popover {
  static instances: Popover[] = [];

  constructor(
    trigger: HTMLElement,
    popover: HTMLElement,
    options: PopoverOptions = {}
  ) {
    this.trigger = trigger;
    this.popover = popover;
    this.options = options;
    this.setup();
    Popover.instances.push(this);
  }

  private trigger: HTMLElement | null = null;
  private popover: HTMLElement | null = null;
  private options: PopoverOptions = {};
  private hideTimeout = 0;

  popper: PopperInstance | null = null;

  private _state: PopoverState = DEFAULT_STATE;

  get state(): PopoverState {
    return this._state;
  }

  set state(nextState: PopoverState) {
    this._state = { ...this._state, ...nextState };

    if (this.options.onChange) {
      this.options.onChange(this.state);
    }
  }

  private setup() {
    // Ensure previously bound listeners are removed
    this.teardown();

    // Show
    this.trigger?.addEventListener("mouseenter", this.handleShowEvent);
    this.trigger?.addEventListener("focusin", this.handleShowEvent);

    // Hide
    this.trigger?.addEventListener("mouseleave", this.handleHideEvent);
    this.trigger?.addEventListener("focusout", this.handleHideEvent);
    this.trigger?.addEventListener("keydown", this.handleKeyDown);

    // Popover events
    this.popover?.addEventListener("mouseenter", this.clearHideTimeout);
    this.popover?.addEventListener("mouseleave", this.handleHideEvent);

    // Popovers
    this.trigger?.addEventListener(
      "requestopen",
      this.handleRequestOpenPopover
    );
    this.trigger?.addEventListener(
      "requestclose",
      this.handleRequestClosePopover
    );
  }

  private teardown() {
    this.trigger?.removeEventListener("mouseenter", this.handleShowEvent);
    this.trigger?.removeEventListener("focusin", this.handleShowEvent);
    this.trigger?.removeEventListener("mouseleave", this.handleHideEvent);
    this.trigger?.removeEventListener("focusout", this.handleHideEvent);
    this.trigger?.removeEventListener("keydown", this.handleKeyDown);
    this.trigger?.removeEventListener(
      "requestopen",
      this.handleRequestOpenPopover
    );
    this.trigger?.removeEventListener(
      "requestclose",
      this.handleRequestClosePopover
    );
    this.popover?.removeEventListener("mouseleave", this.handleHideEvent);
    this.popover?.removeEventListener("mouseenter", this.clearHideTimeout);
  }

  private async create() {
    if (!this.trigger || !this.popover) return;

    const modifiers = [
      {
        name: "flip",
        options: {
          padding: NUBBIN_SIZE,
          fallbackPlacements: [
            "top",
            "top-start",
            "top-end",
            "bottom",
            "bottom-start",
            "bottom-end",
            "right",
            "right-start",
            "right-end",
            "left",
            "left-start",
            "left-end",
          ],
        },
      },
      {
        name: "hide",
        enabled: false,
      },
      {
        name: "preventOverflow",
        options: {
          padding: {
            top: 2,
            bottom: 2,
            left: 5,
            right: 5,
          },
        },
      },
      {
        name: "offset",
        options: {
          offset: ({
            placement,
            reference,
          }: {
            placement: PopperPlacement;
            reference: PopperRect;
          }) => {
            if (placement.match(/^(top|bottom)/)) {
              return [0, NUBBIN_SIZE];
            }

            if (placement.match(/end?/)) {
              return [(reference.height / 2) * 1, NUBBIN_SIZE];
            }

            if (placement.match(/start?/)) {
              return [(reference.height / 2) * -1, NUBBIN_SIZE];
            }

            return [0, NUBBIN_SIZE];
          },
        },
      },
    ];

    const { createPopper } = await import("@popperjs/core");

    this.popper = createPopper(this.trigger, this.popover, {
      // Note: using "fixed" will cause incorrect positioning if any parent has a transform
      // https://blog.intive-fdv.com/betrayal-position-fixed/
      strategy: "fixed",
      placement: this.getPlacement(this.options.placement),
      modifiers,
    });

    this.state = { isReady: true };
  }

  destroy() {
    this.popper?.destroy();
    this.popper = null;
    this.state = {
      ...DEFAULT_STATE,
      isDisabled: this.state.isDisabled,
    };
  }

  async show() {
    if (this.state.isDisabled) return;
    await this.popper?.update();
    this.state = { isOpen: true };
  }

  hide() {
    this.state = { isOpen: false };
  }

  disable() {
    this.state = { isDisabled: true, isOpen: false };
  }

  enable() {
    this.state = { isDisabled: false };
  }

  static closeAll() {
    for (const instance of Popover.instances) {
      instance.hide();
    }
  }

  // Map the "placement" property used by SLDS to the correct Popper "placement"
  private getPlacement(
    placement: PopoverPlacement = DEFAULT_PLACEMENT
  ): PopperPlacement {
    switch (placement) {
      case "top left":
        return "top-start";
      case "top right":
        return "top-end";
      case "right top":
        return "right-start";
      case "right bottom":
        return "right-end";
      case "bottom left":
        return "bottom-start";
      case "bottom right":
        return "bottom-end";
      case "left top":
        return "left-start";
      case "left bottom":
        return "left-end";
      default:
        return placement;
    }
  }

  private handleShowEvent = () => {
    this.clearHideTimeout();
    // Close all other instances to avoid having overlapping popovers
    Popover.closeAll();
    this.create()
      .then(() => {
        // Only fire a show event if a hide event wasn't fired while the popper was being created
        if (!this.state.hideQueued) {
          return this.show();
        }

        this.state = { hideQueued: false };
      })
      .catch((e: Error) => logger.warn(e.message));
  };

  private clearHideTimeout = () => {
    window.clearTimeout(this.hideTimeout);
  };

  private removePopover() {
    // Destroy the instance so consumers aren't required to do cleanup
    this.hide();
    this.destroy();
  }

  private removePopoverWithDelay = () => {
    // For a11y, delay removing popover on mouseleave to give the user
    // time to hover over it.
    this.clearHideTimeout();
    this.hideTimeout = window.setTimeout(
      this.removePopover.bind(this),
      this.options.hideDelay
    );
  };

  private handleHideEvent = () => {
    if (!this.state.isReady) {
      this.state = { hideQueued: true };
    } else {
      this.removePopoverWithDelay();
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      this.removePopover();
    }
  };

  private handleRequestOpenPopover = () => {
    this.disable();
  };

  private handleRequestClosePopover = () => {
    this.enable();
  };
}
